<template>
    <div>
        <div
            :id="'dwf-task-' + task.id"
            class="border rounded"
            :class="[
                (isCompleted ? 'bg-gray-50 shadow-sm' : 'bg-white shadow'), 
                (showNeedsAttentionSetup ? 'border-danger' : ''),
            ]"
        >
            <div class="py-2 px-3 rounded d-flex bg-gray-150 align-items-center justify-content-between">
                <h6 class="mb-0">{{ task.title }}</h6>
                <div class="d-flex align-items-center">
                    <transition name="fade-fast" mode="out-in">
                        <b-badge v-if="showSaveSuccess" key="saveSuccessBadge" variant="bare-primary" class="text-uppercase">Saved</b-badge>
                        <b-spinner v-else-if="loading" key="isLoading" small></b-spinner>
                        <b-badge v-else-if="showNeedsAttentionSetup" key="needsAttentionBadge" variant="danger" class="text-uppercase">Needs attention</b-badge>
                        <b-badge v-else-if="isPending" key="pendingOkBadge" variant="success" class="text-uppercase">Ready</b-badge>
                        <b-badge v-else-if="isActive" key="activeBadge" variant="info" class="text-uppercase">Active</b-badge>
                        <b-badge v-else-if="isCompleted" key="completedBadge" variant="secondary" class="text-uppercase">Completed</b-badge>
                    </transition>
                </div>
                <!-- <div class="font-weight-500 font-size-80 text-uppercase mb-1">
                    {{ task.is_template_task ? 'Auto-Task' : 'Task' }}
                </div>
                <div class="">
                    <transition name="fade-fast" mode="out-in">
                        <div v-if="showSaveSuccess" key="saveSuccessBadge">
                            <b-badge variant="success">SAVED</b-badge>
                        </div>
                        <div v-else-if="isSetupLocked" key="taskIsLocked" class="text-secondary">
                            <span class="font-weight-bold font-size-70 text-uppercase mr-1">Locked</span>
                            <b-icon icon="lock-fill" variant="secondary"></b-icon>
                        </div>
                        <div v-else-if="task.needs_attention_setup || !allInputsValid" key="taskNeedsAttentionSetup" class="text-danger">
                            <span class="font-weight-bold font-size-70 text-uppercase mr-1">Needs Attention</span>
                            <b-icon icon="exclamation-circle" variant="danger"></b-icon>
                        </div>
                        <div v-else key="taskIsOk" class="text-success">
                            <b-icon icon="check-circle" variant="success"></b-icon>
                        </div>
                    </transition>
                </div> -->
            </div>
            <div class="px-3 pb-3 pt-2">

                <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
                
                <!-- <div class="mb-2 d-flex justify-content-between align-items-baseline">
                    <h6 class="mb-0">{{ task.title }}</h6>
                    <div>
                        <b-badge v-if="showSaveSuccess" key="saveSuccessBadge" variant="success" class="text-uppercase">Saved</b-badge>
                        <b-badge v-else-if="isPending && (task.needs_attention_setup || !allInputsValid)" key="needsAttentionBadge" variant="danger" class="text-uppercase">Needs attention</b-badge>
                        <b-badge v-else-if="isPending" key="pendingOkBadge" variant="soft-success" class="text-uppercase">Ready</b-badge>
                        <b-badge v-else-if="isActive" key="activeBadge" variant="primary" class="text-uppercase">Active</b-badge>
                        <b-badge v-else-if="isCompleted" key="completedBadge" variant="success" class="text-uppercase">Completed</b-badge>
                    </div>
                </div> -->
                <p class="small text-muted mb-3">{{ task.instructions }}</p>
                <div v-if="task.is_template_task" class="alert alert-warning">
                    <p class="mb-1">This is an Auto-Task</p>
                    <p class="mb-0 small">Auto-Tasks are created as necessary, depending on the status of other things in the workflow.</p>
                </div>
                <div v-if="showDeadlineSection" class="mt-3">
                    <p class="hot-edit-label mb-1">Deadline</p>
                    <div v-if="showDeadlineDaysInput" key="showDeadlineDaysInput" class="mb-2 d-flex gap-4 justify-content-start align-items-center">
                        <div class="d-flex gap-2 align-items-center flex-shrink-1">
                            <b-form-spinbutton 
                                v-model="localData.deadline_days" 
                                inline 
                                class="mr-2 font-weight-bold"
                                min="1"
                                max="365"
                                :disabled="deadlineDaysInputDisabled"
                                @change="setDirty"
                            ></b-form-spinbutton>
                            <div class="font-size-80 text-wrap flex-shrink-1">
                                days after<br>starting
                            </div>
                        </div>
                        <b-form-checkbox
                            :checked="noDeadline"
                            @input="handleNoDeadlineClick"
                        >None</b-form-checkbox>
                    </div>
                    <div v-else-if="deadlineDisplay" class="mb-3 font-weight-500 font-size-80">
                        {{ deadlineDisplay }}
                    </div>
                </div>
                <!-- <div class="mb-3 d-flex align-items-center justify-content-start">
                    <span class="mr-3 hot-edit-label">Allow late completions?</span>
                    <b-form-radio-group
                        v-model="localData.deadline_type"
                        :disabled="isDisabled"
                        :options="[{value: 'hard', text: 'No'}, {value: 'soft', text: 'Yes'}]"
                    ></b-form-radio-group>
                </div> -->
                <div class="mt-3">
                    <div class="mb-1 d-flex flex-wrap gap-3 align-items-center justify-content-start">
                        <span class="hot-edit-label">Assignees</span>
                        <div v-if="task.showAssigneeInput" class="d-flex flex-wrap gap-2">
                            <b-badge v-if="task.allow_internal_assignees" variant="soft-primary" class="text-uppercase">Internal</b-badge>
                            <b-badge v-if="task.allow_external_assignees" variant="soft-danger" class="text-uppercase">External</b-badge>
                        </div>
                    </div>
                    <div v-if="task.showAssigneeInput" key="assigneeInputArea" class="">
                        <MultiSelect 
                            v-if="canChangeAssignedTus"
                            key="assignedTusInput"
                            v-model="localData.assigned_tus" 
                            :options="task.possible_assigned_tus" 
                            dataKey="id"
                            placeholder="Select assignees..." 
                            display="chip"
                            :disabled="assignedTusInputDisabled"
                            class="w-100"
                            @hide="debouncedUpdate"
                            @change="setDirty"
                        >
                            <template #indicator>
                                <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>  <!-- had to add this slot, even though it has default -->
                            </template>
                            <template #value="slotProps">
                                <div class="d-flex flex-wrap gap-2">
                                    <!-- <b-badge variant="soft-primary" v-for="option of slotProps.value" :key="option.id">
                                        <div>{{option.full_name}}</div>
                                    </b-badge> -->
                                    <tenant-user-badge
                                        v-for="option in slotProps.value"
                                        :key="option.id"
                                        :tu-data="option"
                                    ></tenant-user-badge>
                                </div>
                                
                                <template v-if="!slotProps.value || slotProps.value.length === 0">
                                    Select Assignees
                                </template>
                            </template>
                            <template #option="slotProps">
                                {{ slotProps.option.full_name }}
                                <b-badge :variant="'soft-' + (slotProps.option.is_internal ? 'primary' : 'danger')" class="text-uppercase ml-2 small">{{ slotProps.option.is_internal ? 'Internal' : 'External'}}</b-badge>
                            </template>
                        </MultiSelect>
                        <div v-else key="assignedTusDisplayOnly" class="d-flex flex-wrap gap-2">
                            <!-- <b-badge variant="soft-primary" v-for="option of slotProps.value" :key="option.id">
                                <div>{{option.full_name}}</div>
                            </b-badge> -->
                            <tenant-user-badge
                                v-for="tu in task.assigned_tus"
                                :key="tu.id"
                                :tu-data="tu"
                            ></tenant-user-badge>
                        </div>
                        <div v-if="!assignedTusValid" class="text-danger small">Must assign people to this task</div>
                    </div>
                    <div v-else class="font-italic font-size-80 text-muted">
                        Assignees set automatically
                    </div>
                </div>
                <!-- <p class="small text-muted mb-0">{{ task.sm_state }}</p> -->
                <!-- <div 
                    v-if="task.showSaveButton && (hasChanges || showSaveSuccess)"
                    class="mt-3"
                >
                    <b-button
                        variant="subtle-primary"
                        block
                        size="sm"
                        :disabled="isSaveDisabled || !hasChanges"
                        @click="updateTask"
                    >
                        <span v-if="showSaveSuccess" key="taskSaveSuccess" class="text-success font-weight-bold">SAVED</span>
                        <span v-else key="notSaveSuccess">
                            {{ hasChanges ? 'Save Changes' : 'No Changes' }}
                        </span>
                    </b-button>
                </div> -->
            </div>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { DwfTask } from './workflow_models';
import pick from 'lodash/pick';
import toInteger from 'lodash/toInteger';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { HumanizeDateTimeHelper } from '../../js/filters-helpers';
import { DateTime } from 'luxon';


export default {
    name: 'WorkflowTaskSetup',
    components: {},
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        task: {  // should be instance of DwfTask -- we pass back the same in @updated
            type: DwfTask,  // we'll get warning in non-production env if it is not instance of this
            required: true,
        },
        readOnly: {  // for displaying only
            type: Boolean,
            default: false,
        },
        disabled: {  // if you want to show form components, but disabled from parent
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            localUpdateFields: ['assigned_tus', 'deadline_days', 'deadline_type'],  // the things we'll update locally
            localData: null,  // make a copy of the things you are updating locally in created() so you don't mutate parent
            // but only the things you're updating.... for everything else, just use value from prop in case parent updates it
            lastSavedLocalData: null,  // same format as localData -- the snapshot from last saved to flag if has changes
            noDeadline: false,  // v-model with the checkbox for setting 'no deadline'
            showSaveSuccess: false,  
            isDirty: false,  // dirty flag, set true if user makes a change... even if no changes but has isDirty, we'll show SAVED indicator to user to make them feel warm and fuzzy knowing its saved
        }
    },
    watch: {
        task: {
            handler: function(newTask, oldTask) {
                // look for changes to any of the things we're managing locally.... if has those changes, then we have to make another local copy
                const getImportantStuff = (taskObj) => pick(taskObj, this.localUpdateFields);
                const newStuff = getImportantStuff(newTask);
                const oldStuff = getImportantStuff(oldTask);
                if (!isEqual(newStuff, oldStuff)) {
                    // if something changed, we need to set new local data
                    // even if we have local changes, should we do it?  Yes... should honor what parent has
                    console.log(`SOMETHING IMPORTANT CHANGED IN PARENT FOR TASK ID ${newTask.id}`);
                    console.log('newStuff', newStuff);
                    console.log('oldStuff', oldStuff);
                    this.setLocalData(newTask);
                }
            },
            deep: true,  // need to watch for deep changes... in case object itself is not changed, but it likely will be changed... still, use deep: true
        },
        'localData.deadline_days': {
            // ONLY WATCHING DEADLINE DAYS HERE 
            // for assigned_tus, we'll trigger on closing the multiselect, otherwise things look janky when doing updates when its still open
            handler: function() {
                // watch for any changes in localData, 
                // if we have changes, and if not equal to lastSaved, and all things valid?, then make API call to update 
                // use debounced update method 
                //const isChangedSinceLastSaved = !isEqual(this.lastSavedLocalData, this.localData);
                console.log('from localData watcher');
                this.debouncedUpdate();
                // we'll do a nextTick so computed can update, then use those to check things
                /* MOVED ALL THIS TO THE FUNCTION WE DEBOUNCED
                this.$nextTick(() => {
                    if (this.hasChanges && this.allInputsValid) {
                        console.log('hasChanges AND allInputsValid -- DO API CALL');
                        this.debouncedUpdate();
                    } else if (!this.hasChanges) {
                        console.log('not changed');
                    } else if (!this.allInputsValid) {
                        console.log('inputs are not valid');
                    }
                }); */
            },
            //deep: true,
        },
    },
    computed: {
        hasChanges() {
            // return boolean if there are any changes since last saved
            return !isEqual(this.lastSavedLocalData, this.localData);
        },
        isWorkflowOwner() {
            // is the current TU the workflow owner?
            return this.task.workflow_owner_id === this.$currentUserInfo.tenant_user_id;
        },
        assignedTusValid() {  // must be based on LOCAL data
            const assignedTusNotRequired = this.task.showAssigneeInput === false;
            // if this is explicitly false, then assigned_tus is not required
            // update this with a real field from backend!
            return assignedTusNotRequired || (!!this.localData?.assigned_tus && !!this.localData.assigned_tus.length);
            // either its not required, or we have at least one
        },
        deadlineDaysValid() {
            const deadlineDaysNotRequired = this.task.canSetDeadlineDays === false;
            const noDeadlineIsChecked = this.noDeadline;
            return deadlineDaysNotRequired || !!this.localData?.deadline_days || noDeadlineIsChecked;  // this won't allow zero
            // should we check types too?  that its a number/int?
        },
        allInputsValid() {
            // are all inputs valid so we can send updated data to backend?
            // validation must be based on LOCAL data, because we use this to decide if we should send update request or not... and props only get updated after we make update request, emit to parent, then get updated props from parent
            return this.assignedTusValid && this.deadlineDaysValid;
        },
        showNeedsAttentionSetup() {
            return !!this.isPending && (!!this.task?.needs_attention_setup || !this.allInputsValid);
        },
        isPending() {
            return this.task.sm_state === 'pending';
        },
        isStarted() {
            return this.task.is_started;
        },
        isActive() {
            return this.task.sm_state === 'active';
        },
        isCompleted() {
            return this.task.sm_state === 'completed';
        },
        showDeadlineSection() {
            // should we show the deadline section at all?
            // should be true if we can set the input, or if could ever set the input before
            return this.task.canSetDeadlineDays;  // replace with real @property from backend
        },
        showDeadlineDaysInput() {
            // task must allow setting it, and must be in 'pending' state (not yet started)
            // do not set false for loading... that's separate... otherwise things will flash that don't make sense
            return this.task.canSetDeadlineDays && !this.isStarted;
        },
        deadlineDisplay() {
            // formatted text for the deadline - only shown if isStarted
            if (this.task?.sm_state === 'active') {
                if (!this.task.current_deadline) return 'Not Set';
                const deadlineIso = this.task.current_deadline ?? '';
                const luxonDeadline = deadlineIso ? DateTime.fromISO(deadlineIso) : null;
                const deadline = (!!luxonDeadline && luxonDeadline.isValid) ? luxonDeadline.toLocaleString(DateTime.DATETIME_SHORT) : '';
                const howFarAway = HumanizeDateTimeHelper(this.task.current_deadline);
                return `${deadline} (${howFarAway})`;
            } else if (this.task.sm_state === 'completed') {
                // if not active, just show empty string -- if pending, we'll show deadline days input
                return 'Already Completed';
            } else {
                return 'Not Applicable';
            }
        },
        isSaveDisabled() {
            return this.loading || this.isSetupLocked || this.readOnly || this.disabled;
        },
        isSetupLocked() {
            // we'll completely lock things if the task is already COMPLETED or SKIPPED (or similar)
            const lockStates = ['completed', 'cancelled', 'skipped'];
            return lockStates.includes(this.task.sm_state);
        },
        deadlineDaysInputDisabled() {
            // does not control visibility of input, just the disabled state - other one for showing it or not
            return this.loading || this.readOnly || this.disabled || this.noDeadline;
        },
        canChangeAssignedTus() {
            // we can still change assigned TUs even if task has started (must still be active)
            // don't use this for disabled too... just for showing the input or not
            const shouldShow = this.task.showAssigneeInput === true;  // checks if its an auto_field, if not, then its true
            const stateOk = this.task.state_dict?.can_change_assigned_tus;
            return shouldShow && stateOk && this.isWorkflowOwner;
            //return stateOk && !this.loading && !this.readOnly && !this.disabled;
        },
        assignedTusInputDisabled() {
            return !this.canChangeAssignedTus || this.loading || this.readOnly || this.disabled;
        }
    },
    created() {
        // should we check task prop to make sure its instance of DwfTask, then if not, we make it one?
        // we set that class as type in prop, so should be OK for now
        this.setLocalData(this.task);
        // setup debounced save method - then call from watcher on localData
        this.debouncedUpdate = debounce(this.updateTask, 750);
    },
    mounted() {
        
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        handleTaskUpdated(taskApiData) {
            // pass in new data object from API received after update, 
            // then pass it to parent for it to handle, and/or do something locally if needed
            console.log('raw data from api', taskApiData);
            //this.$set(this.tasksById, newTaskData.id, new DwfTask(taskApiData));  // using vue.$set can't hurt... 
            const newTask = new DwfTask(taskApiData);
            // update our local copy and last saved
            // this.setLocalData(newTask);  // not doing this since we implemented watcher... pass new stuff to parent, then parent will pass it back down, our watcher sees it and updates local
            console.log('emitting newTask to parent', newTask);
            this.$emit('updated', newTask);
            // IN PARENT this.checkCurrentTab();  // recheck the tab for validation, etc.
        },
        setLocalData(taskData) {
            // pass in a full task object (DwfTask), this pulls off the things we want to manage locally
            // this sets the local copy and decoupled copy for storing last saved
            // but NOT YET formatted for sending back to API
            const taskClone = cloneDeep(taskData);  // cloneDeep to make sure any nested objects are fully decoupled even after pick
            this.lastSavedLocalData = pick(taskClone, this.localUpdateFields);
            this.localData = cloneDeep(this.lastSavedLocalData);
            // set the local 'noDeadline' thing - toggling it will change this.localData.deadline_days to null, or last saved value
            this.noDeadline = (this.localData.deadline_days === null); 
            this.clearDirty();  // reset this
            // we set lastSaved first, because our watcher on localData will compare with lastSaved when localData changes... since they are right next to each other it probably doesn't matter, but just in case
            return this.localData;  // in case you need it where called
        },
        transformLocalDataForApi(localData) {
            // DRY way to take localData or lastSavedLocalData and transform it to what we send to API
            let saveData = cloneDeep(localData);  // start with this
            const saveDataKeys = Object.keys(saveData);
            if (saveDataKeys.includes('assigned_tus')) {
                saveData.assigned_tus = saveData.assigned_tus.map(tuObj => tuObj.id);  // must be IDs, not objects
            }
            if (saveDataKeys.includes('deadline_days')) {
                saveData.deadline_days = saveData.deadline_days ? toInteger(saveData.deadline_days) : null;  // coerce to int
            }
            return saveData;
        },
        prepTaskDataForSave() {
            // get from this.task, prep it and return it
            //const allowedUpdateFields = ['assigned_tus', 'deadline_days', 'deadline_type'];
            //let saveData = pick(this.task, allowedUpdateFields);
            const preppedLocalData = this.transformLocalDataForApi(this.localData);
            const preppedLastSaved = this.transformLocalDataForApi(this.lastSavedLocalData);
            let changedData = {};
            const possibleKeys = Object.keys(preppedLocalData);
            possibleKeys.forEach(key => {
                const val = preppedLocalData[key];  // the current value
                const lastSavedVal = preppedLastSaved[key];  // last saved value
                if (!isEqual(val, lastSavedVal)) {  // if they are not equal, put it in the stuff to save
                    changedData[key] = val;
                }
            })
            return changedData;
        },
        async updateTask() {
            // check things before actually performing the update
            // this is the method we debounce... so we only do these checks when the debounce has expired and we're ready to call the update
            if (this.hasChanges) {
                console.log('hasChanges AND allInputsValid -- DO API CALL');
                // actually... we should do it even if not all valid... otherwise backend and parent component get out of sync with this component... 
                // this could have different local state than parent or backend, and will confuse user... just always keep them in sync
                await this.performUpdateTask();
            } else if (!this.hasChanges) {
                console.log('not changed');
                //this.triggerSaveSuccess();  // show it here also?, so user knows their changes matched saved values, even though we didn't have to update anything
                // we'll use a dirty flag instead... if no changes, but is dirty, then show SAVED
                if (this.isDirty) {
                    this.triggerSaveSuccess();
                }
            } /* else if (!this.allInputsValid) {
                console.log('inputs are not valid');
            } */
        },
        async performUpdateTask() {
            // now need to format correctly -- pull out TU ids, set deadline correctly
            // just v-model from inputs to the top level data, then grab it here
            // IMPORTANT - only send what has changed vs. last saved!  
            // because some things can be updated while task is active, some cannot
            // and in our DRF view we validate anything in the posted data
            // so if you didn't change something but passed it in and it cannot be changed now, the whole thing gets rejected
            const taskUpdateData = this.prepTaskDataForSave();
            this.loading = true;
            try {
                const { data } = await suppliosApi.workflowTask.update_task_setup(this.task.id, taskUpdateData);
                this.handleTaskUpdated(data);  // will only be data for the ONE task we updated
                this.triggerSaveSuccess();
                // this is kindof annoying... expecially when we show save badge on each task card
                // this.$toast.success('Task updated');
            } catch (err) {
                this.apiErrorHandler(err);
            }
            console.log('I AM DONE');
            this.loading = false;
        },
        triggerSaveSuccess() {
            this.showSaveSuccess = true;
            setTimeout(() => {
                this.showSaveSuccess = false;
            }, 2000);
        },
        setDirty() {
            this.isDirty = true;
            // also tell parent that something was changed, only exists in local here, don't yet know if its valid
            this.$emit('dirty', true);
            // actually... likely not using this... because we don't know which task was dirty and when... so not sure when to clear the dirty flag in parent
        },
        clearDirty() {
            this.isDirty = false;
        },
        handleNoDeadlineClick(checkboxValue) {
            // update deadline_days and disable input if needed
            console.log('checkboxValue', checkboxValue);
            if (checkboxValue) {
                this.localData.deadline_days = null;
                this.noDeadline = true;
            } else {
                this.localData.deadline_days = this.lastSavedLocalData.deadline_days;
                this.noDeadline = false;
            }
        },
    }
}
</script>

<style scoped lang="scss">

.b-form-spinbutton {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: fit-content;
}

</style>